import React from 'react'
import { Button, message } from 'antd'
import { observer } from 'mobx-react'
import { ColumnsType } from 'antd/es/table'
import { t } from '../../config/i18n'
import { store } from '../../store'
import { routes } from '../../config/routes'
import { Table } from '../shared/ui/Table'
import { PageSettings } from '../shared/ui/PageSettings'
import { useParams } from 'react-router'
import { useNavigate } from '../../hooks/useNavigate'
import { useCatalog } from '../../hooks/useCatalog'
import { hasAccess } from '../../lib/utilsCatalogs'
import { modalConfirm, modalSelectBusiness } from '../../lib/utilsUI'
import { TSingleCatalogConfig } from '../../models/SingleCatalogConfig'
import { TableActionsProps, TExtraAction } from '../shared/ui/table/Actions'
import { PERMISSIONS, TAbstractCatalogConfig } from '../../models/AbstractCatalogConfig'
import { AbstractCatalogService } from '../../services/AbstractCatalogService'
import { SingleCatalogService } from '../../services/SingleCatalogService'

export const PageCatalogList = observer(() => {
  const navigate = useNavigate()
  const { type } = useParams()
  const { catalogService, texts } = useCatalog(type, 'catalog_list')
  const itemLoadingId = catalogService.store.itemLoadingId

  React.useEffect(() => {
    catalogService.loadConfigItems().catch(window.logger.error)
  }, [catalogService])

  const onDelete = async (item: TAbstractCatalogConfig) => {
    if (hasAccess(item, PERMISSIONS.CATALOG_DELETE)) {
      modalConfirm({
        okText: t.common.delete,
        onOk: async () => {
          await catalogService.deleteConfigItem(item)
          await catalogService.loadConfigItems(true)
        },
      })
    } else {
      message.error(t.common.accessDenied)
    }
  }

  const columns: ColumnsType<TAbstractCatalogConfig> = [
    {
      key: 'name',
      dataIndex: 'name',
      title: t.common.name,
      render: (_, item) => (
        <Button
          type="link"
          disabled={catalogService.store.itemLoadingId === item.id}
          onClick={() => navigate(routes.catalogItemList, { type, id: item.id, parentId: '0' })}
        >
          {item.name}
        </Button>
      )
    },
  ]

  const tableActions = (item: TSingleCatalogConfig): TableActionsProps => ({
    loading: itemLoadingId === item.id,
    onSettings: () => navigate(routes.catalogConfig, { type, id: item.id }),
    onDelete: () => onDelete(item),
    extra: createTableExtraActions(item, catalogService),
  })

  return  (
    <PageSettings
      loading={store.ui.loading}
      title={texts.titleCatalogList}
      actionText={t.catalog.addButton}
      onAction={() => navigate(routes.catalogConfig, { type, id: 'new' })}
      extraActions={
        <Button onClick={() => catalogService.importConfig()}>{t.catalog.importConfig}</Button>
      }
    >
      <Table
        idField={'id'}
        items={catalogService.store.configItemList}
        columns={columns}
        actions={tableActions}
      />
    </PageSettings>
  )
})

function createTableExtraActions (item: TAbstractCatalogConfig, catalogService: AbstractCatalogService<any, any>): TExtraAction[] {
  const result: TExtraAction[] = [
    [t.catalog.exportConfig, () => catalogService.exportConfig(item)],
    [t.catalog.importItems, () => catalogService.importItems(item)],
    [t.catalog.exportItems, () => catalogService.exportItems(item)],
  ]

  if (catalogService instanceof SingleCatalogService) {
    result.push(
      [t.catalog.importBindings, async () => catalogService.importBindings(item, (await modalSelectBusiness()).id)],
      [t.catalog.exportBindings, async () => catalogService.exportBindings(item, (await modalSelectBusiness()).id)],
    )
  }
  return result
}
